import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, X, Shield, Zap, Sparkles } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

export default function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [isAnnual, setIsAnnual] = useState(true);
  const isMobile = useIsMobile();

  // MODIFIED: Updated CTA copy for professional MoR review and waitlist strategy, added isDisabled flag
  const plans = [
    {
      name: "Free",
      description: "Essential safety for daily commutes.",
      price: "0",
      period: "forever",
      cta: "Get Started Free",
      popular: false,
      isDisabled: false, // NEW: Free plan must be interactive at launch
      features: [
        { name: "Active Trips", value: "1 at a time", included: true },
        { name: "Scheduled Trips", value: "1 future trip", included: false },
        { name: "Trusted Groups", value: "1 Group", included: false },
        { name: "Contacts per Group", value: "3 People", included: false },
        { name: "Alert Method", value: "Email Only", included: false },
        { name: "Emergency Attachments", value: "Text Only", included: false },
      ],
    },
    {
      name: "Pro",
      description: "Advanced protection for your everyday peace of mind.",
      price: isAnnual ? "7.00" : "9.99",
      period: "per month",
      billingNote: isAnnual ? "Billed $84 yearly" : "Billed monthly",
      cta: "Join Pro Waitlist", // MODIFIED: Professional SaaS fallback instead of 'Coming Soon'
      popular: true,
      isDisabled: false, // NEW: Keep enabled to trigger a Waitlist Modal/Email capture instead of a dead button
      features: [
        { name: "Active Trips", value: "1 at a time", included: true },
        { name: "Scheduled Trips", value: "Up to 5 trips", included: true },
        { name: "Trusted Groups", value: "Up to 5 Groups", included: true },
        { name: "Contacts per Group", value: "7 People", included: true },
        { name: "Alert Method", value: "Email + SMS", included: true },
        { name: "Emergency Attachments", value: "Media attachments", included: true },
      ],
    },
  ];

  const handlePlanAction = (planName: string) => {
    const waitlistSection = document.getElementById('waitlist');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '#waitlist';
    }
  };

  return (
    <section id="pricing" ref={ref} className="relative py-32 md:py-40 bg-slate-50 overflow-hidden">
      <div className="container mx-auto px-6 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 0 : 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: isMobile ? 0.4 : 0.8 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-xs font-medium uppercase tracking-[0.2em] text-emerald-600 mb-4">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Simple, transparent<br /><span className="text-emerald-600">pricing</span>
          </h2>

          {!isMobile && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>Monthly</span>
              <button
                aria-label="Toggle annual billing"
                onClick={() => setIsAnnual(!isAnnual)}
                className="relative w-14 h-7 rounded-full bg-slate-200 transition-colors"
              >
                <span className="sr-only">Toggle annual billing</span>
                <motion.div
                  className="absolute top-1 left-1 w-5 h-5 rounded-full bg-emerald-500 shadow-sm"
                  animate={{ x: isAnnual ? 26 : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>
                Annually
              </span>
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                Save 30%
              </span>
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <React.Fragment key={plan.name}>
              {isMobile && plan.name === 'Pro' && (
                <div className="flex items-center justify-center gap-4 mt-4 mb-2">
                  <span className={`text-sm font-medium ${!isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>Monthly</span>
                  <button
                    aria-label="Toggle annual billing"
                    onClick={() => setIsAnnual(!isAnnual)}
                    className="relative w-14 h-7 rounded-full bg-slate-200 transition-colors"
                  >
                    <span className="sr-only">Toggle annual billing</span>
                    <motion.div
                      className="absolute top-1 left-1 w-5 h-5 rounded-full bg-emerald-500 shadow-sm"
                      animate={{ x: isAnnual ? 26 : 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </button>
                  <span className={`text-sm font-medium ${isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>
                    Annually
                  </span>
                  <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    Save 30%
                  </span>
                </div>
              )}
            <motion.div
              initial={{ opacity: 0, y: isMobile ? 0 : 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: isMobile ? 0.4 : 0.6, delay: isMobile ? 0 : index * 0.2 }}
              className={`relative rounded-3xl p-8 flex flex-col ${
                plan.popular
                  ? 'bg-slate-900 text-white shadow-2xl shadow-slate-900/20'
                  : 'bg-white border border-slate-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-emerald-500 text-white text-sm font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  Recommended
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${plan.popular ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-100 text-slate-600'}`}>
                  {plan.popular ? <Zap className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
                </div>
                <h3 className="text-xl font-bold">{plan.name}</h3>
              </div>

              <p className={`text-sm mb-6 ${plan.popular ? 'text-white/60' : 'text-slate-500'}`}>
                {plan.description}
              </p>

              <div className="mb-8">
                <div className="flex items-end gap-1">
                  <span className="text-5xl font-bold">${plan.price}</span>
                  <span className={`text-sm mb-2 ${plan.popular ? 'text-white/50' : 'text-slate-400'}`}>/{plan.period}</span>
                </div>
                <p className={`text-xs mt-1 ${plan.popular ? 'text-white/40' : 'text-slate-400'}`}>{plan.billingNote}</p>
              </div>

              {/* MODIFIED: Removed hardcoded disabled & ternary text. Dynamic styling based on plan type */}
              <button
                disabled={plan.isDisabled}
                onClick={() => handlePlanAction(plan.name)}
                className={`w-full py-3.5 rounded-xl font-medium mb-8 transition-all duration-200 ${
                  plan.isDisabled
                    ? 'opacity-50 cursor-not-allowed bg-slate-100 text-slate-400'
                    : plan.popular
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 active:scale-[0.98]'
                    : 'bg-slate-900 hover:bg-slate-800 text-white active:scale-[0.98]'
                }`}
              >
                {plan.cta}
              </button>

              <div className="space-y-4 flex-grow">
                <p className={`text-xs font-semibold uppercase tracking-wider ${plan.popular ? 'text-white/40' : 'text-slate-400'}`}>
                  What's included
                </p>
                {plan.features.map((feature) => (
                  <div key={feature.name} className="flex items-center gap-3">
                    <div className={`flex-shrink-0 rounded-full p-1 ${feature.included ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                      {feature.included ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    </div>
                    <div>
                      <span className={`text-sm ${feature.included ? (plan.popular ? 'text-white' : 'text-slate-700') : 'text-slate-400'}`}>
                        {feature.name}
                      </span>
                      <span className={`block text-xs ${plan.popular ? 'text-white/50' : 'text-slate-400'}`}>
                        {feature.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}